/** Default and per-section SEO copy for BuffaloBossMM (slots, fishing, Shan Koe Mee, Bugyi, live casino, card games). */

/** Protocol + host (no trailing slash). Uses the live URL so one build works on BuffaloBossMM.biz, .cc, .tel, etc. */
export function getSiteOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

/**
 * Absolute canonical URL for a route pathname (must match router SEO / index.html bootstrap).
 * @param {string} [path] pathname e.g. /home (query/hash ignored)
 */
export function getCanonicalUrlForPath(path) {
  const pathname = ((path || '/').split('?')[0] || '/').replace(/\/+$/, '') || '/';
  const origin = getSiteOrigin();
  if (!origin) return '';
  return pathname === '/' ? origin : `${origin}${pathname}`;
}

const BASE_DESC =
  'BuffaloBossMM offers online slots, fishing arcade games, Shan Koe Mee (Shan poker), Bugyi and classic card games, plus live casino with real dealers. Play securely, fast deposits and withdrawals, promotions for Myanmar players.';

const BASE_KEYWORDS =
  'BuffaloBossMM, online slots, slot games, fishing game, fishing arcade, Shan Koe Mee, Shan poker, ဘူဂျီ, Bugyi, Bugyee, online card games, live casino, live dealer, baccarat, roulette, Myanmar online casino, jackpot slots';

export const siteDefaults = {
  title: 'BuffaloBossMM | Online Slots, Fishing, Shan Koe Mee, Bugyi, Live Casino & Card Games',
  description: BASE_DESC,
  keywords: BASE_KEYWORDS,
};

export const pageMeta = {
  home: siteDefaults,

  slots: {
    title: 'Online Slot Games | BuffaloBossMM — Jackpots & Popular Slots',
    description:
      'Play hundreds of online slot games at BuffaloBossMM: classic reels, video slots, jackpots and bonus features. Fast play, fair results, mobile-friendly.',
    keywords: `${BASE_KEYWORDS}, video slots, jackpot slots, slot bonus`,
  },

  fishing: {
    title: 'Fishing Arcade Games Online | BuffaloBossMM',
    description:
      'Shoot-and-catch fishing arcade games at BuffaloBossMM. Colorful tables, multipliers and real-time action—popular in Myanmar and Southeast Asia.',
    keywords: `${BASE_KEYWORDS}, fishing shooter game, arcade fishing`,
  },

  liveCasino: {
    title: 'Live Casino & Live Dealer Games | BuffaloBossMM',
    description:
      'Live casino at BuffaloBossMM: real dealers, HD streams, baccarat, roulette and more. Feel like a real casino floor from your phone or desktop.',
    keywords: `${BASE_KEYWORDS}, live baccarat, live roulette, HD casino stream`,
  },

  shanKoeMee: {
    title: 'Shan Koe Mee Online Rooms | BuffaloBossMM',
    description:
      'Join Shan Koe Mee (Shan poker) tables at BuffaloBossMM. Play with friends or match online—authentic card rules and smooth gameplay.',
    keywords: `${BASE_KEYWORDS}, Shan Koe Mee room, Shan poker online`,
  },

  bugyee: {
    title: 'Bugyi (Bugyee) Card Game Online | BuffaloBossMM',
    description:
      'Play Bugyi / Bugyee card games online at BuffaloBossMM. Traditional Myanmar-style card play with reliable tables and quick rounds.',
    keywords: `${BASE_KEYWORDS}, Bugyi online, Bugyee card game`,
  },

  cardGames: {
    title: 'Online Card Games & Tables | BuffaloBossMM',
    description:
      'Card games and table games at BuffaloBossMM alongside slots and live casino. One account for multiple game types.',
    keywords: `${BASE_KEYWORDS}, table games, multiplayer cards`,
  },

  arcade: {
    title: 'Arcade & Casual Games | BuffaloBossMM',
    description: 'Arcade and casual games at BuffaloBossMM—quick fun between slots, fishing and live casino sessions.',
    keywords: `${BASE_KEYWORDS}, arcade games online`,
  },

  promotion: {
    title: 'Casino Promotions & Bonuses | BuffaloBossMM',
    description:
      'Latest BuffaloBossMM promotions: welcome offers, reload bonuses and cashback for slots, live casino and card games. Check terms on each offer.',
    keywords: `${BASE_KEYWORDS}, casino bonus, welcome bonus, cashback`,
  },

  login: {
    title: 'Member Login | BuffaloBossMM',
    description: 'Log in to your BuffaloBossMM account to play slots, fishing, Shan Koe Mee, Bugyi, live casino and card games.',
    keywords: `${BASE_KEYWORDS}, BuffaloBossMM login`,
  },

  register: {
    title: 'Register & Join | BuffaloBossMM',
    description:
      'Create a BuffaloBossMM account in minutes. Access slots, fishing games, Shan Koe Mee, Bugyi, live casino and secure banking.',
    keywords: `${BASE_KEYWORDS}, BuffaloBossMM register, sign up`,
  },

  contact: {
    title: 'Contact Us | BuffaloBossMM',
    description: 'Contact BuffaloBossMM support for account, deposits, withdrawals and game questions.',
    keywords: `${BASE_KEYWORDS}, BuffaloBossMM support, customer service`,
  },

  rule: {
    title: 'Rules & Terms | BuffaloBossMM',
    description: 'Rules, terms and responsible play information for BuffaloBossMM members.',
    keywords: `${BASE_KEYWORDS}, terms and conditions`,
  },

  deposit: {
    title: 'Deposit | BuffaloBossMM',
    description: 'Deposit funds securely to play slots, fishing, live casino and card games at BuffaloBossMM.',
    keywords: `${BASE_KEYWORDS}, deposit`,
    robots: 'noindex, nofollow',
  },

  withdraw: {
    title: 'Withdraw | BuffaloBossMM',
    description: 'Withdraw your winnings from BuffaloBossMM with supported payment methods.',
    keywords: `${BASE_KEYWORDS}, withdraw`,
    robots: 'noindex, nofollow',
  },

  banking: {
    title: 'Banking | BuffaloBossMM',
    description: 'Banking and payment options for BuffaloBossMM members.',
    keywords: `${BASE_KEYWORDS}, banking`,
    robots: 'noindex, nofollow',
  },

  history: {
    title: 'Transaction History | BuffaloBossMM',
    description: 'View your BuffaloBossMM transaction history.',
    robots: 'noindex, nofollow',
  },

  setting: {
    title: 'Account Settings | BuffaloBossMM',
    description: 'Manage your BuffaloBossMM account settings.',
    robots: 'noindex, nofollow',
  },

  betslip: {
    title: 'Betslip History | BuffaloBossMM',
    description: 'Your betslip history at BuffaloBossMM.',
    robots: 'noindex, nofollow',
  },

  liveScore: {
    title: 'Live Score | BuffaloBossMM',
    description: 'Live scores and sports updates at BuffaloBossMM.',
    keywords: BASE_KEYWORDS,
  },

  matchResult: {
    title: 'Football Match Results | BuffaloBossMM',
    description: 'View today and yesterday football match results at BuffaloBossMM.',
    keywords: `${BASE_KEYWORDS}, football results, match results`,
  },

  maungFb: {
    title: 'Maung FB | BuffaloBossMM',
    description: 'Maung FB at BuffaloBossMM.',
    keywords: BASE_KEYWORDS,
  },

  bodyFb: {
    title: 'Body FB | BuffaloBossMM',
    description: 'Body FB at BuffaloBossMM.',
    keywords: BASE_KEYWORDS,
  },

  videoLinks: {
    title: 'Video Links | BuffaloBossMM',
    description: 'Video links and guides at BuffaloBossMM.',
    keywords: BASE_KEYWORDS,
  },

  commission: {
    title: 'Commission | BuffaloBossMM',
    description: 'Commission program at BuffaloBossMM.',
    robots: 'noindex, nofollow',
  },

  level: {
    title: 'VIP Levels | BuffaloBossMM',
    description: 'VIP levels and rewards at BuffaloBossMM.',
    keywords: `${BASE_KEYWORDS}, VIP`,
  },

  guides: {
    title: 'App Install Guide | BuffaloBossMM',
    description: 'Install the BuffaloBossMM app on Android or iOS for slots, fishing, live casino and card games.',
    keywords: `${BASE_KEYWORDS}, BuffaloBossMM app, install guide`,
  },

  androidGuide: {
    title: 'Android App Install | BuffaloBossMM — Slots, Fishing & Live Casino',
    description:
      'Install BuffaloBossMM on Android: play online slots, fishing games, Shan Koe Mee, Bugyi and live casino on your phone.',
    keywords: `${BASE_KEYWORDS}, BuffaloBossMM Android APK`,
  },

  iosGuide: {
    title: 'iOS App Install | BuffaloBossMM — Slots, Fishing & Live Casino',
    description:
      'Install BuffaloBossMM on iPhone or iPad: slots, fishing arcade, card games and live dealer casino.',
    keywords: `${BASE_KEYWORDS}, BuffaloBossMM iOS app`,
  },

  dreamBooks: {
    title: 'Dream Books | BuffaloBossMM',
    description: 'Dream books feature at BuffaloBossMM.',
    keywords: BASE_KEYWORDS,
  },

  twoD: {
    title: '2D | BuffaloBossMM',
    description: '2D games at BuffaloBossMM.',
    keywords: BASE_KEYWORDS,
  },

  threeD: {
    title: '3D | BuffaloBossMM',
    description: '3D games at BuffaloBossMM.',
    keywords: BASE_KEYWORDS,
  },

  landing: {
    title: 'BuffaloBossMM — Slots, Fishing, Live Casino & Card Games',
    description: BASE_DESC,
    keywords: BASE_KEYWORDS,
  },

  iframe: {
    title: 'Game | BuffaloBossMM',
    robots: 'noindex, nofollow',
  },

  verify: {
    title: 'Verify Account | BuffaloBossMM',
    robots: 'noindex, nofollow',
  },

  dataTable: {
    title: 'Data | BuffaloBossMM',
    robots: 'noindex, nofollow',
  },
};
